
import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';

interface SitemapUrl {
  loc: string;
  lastmod: string;
  changefreq: string;
  priority: string;
}

export default function SitemapPage() {
  const [sitemapUrls, setSitemapUrls] = useState<SitemapUrl[]>([]);
  const [xmlContent, setXmlContent] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const baseUrl = window.location.origin;

  // 静的ページの定義
  const staticPages: SitemapUrl[] = [
    { loc: '/', lastmod: new Date().toISOString().split('T')[0], changefreq: 'daily', priority: '1.0' },
    { loc: '/blog', lastmod: new Date().toISOString().split('T')[0], changefreq: 'daily', priority: '0.9' },
    { loc: '/document', lastmod: new Date().toISOString().split('T')[0], changefreq: 'monthly', priority: '0.7' },
    { loc: '/estimate', lastmod: new Date().toISOString().split('T')[0], changefreq: 'monthly', priority: '0.7' },
    { loc: '/contact', lastmod: new Date().toISOString().split('T')[0], changefreq: 'monthly', priority: '0.6' },
  ];

  useEffect(() => {
    fetchBlogPosts();
  }, []);

  const fetchBlogPosts = async () => {
    try {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('slug, updated_at, published_date')
        .order('published_date', { ascending: false });

      if (error) throw error;

      const blogUrls: SitemapUrl[] = (data || []).map((post) => ({
        loc: `/blog/${post.slug}`,
        lastmod: post.updated_at 
          ? new Date(post.updated_at).toISOString().split('T')[0]
          : new Date(post.published_date).toISOString().split('T')[0],
        changefreq: 'weekly',
        priority: '0.8',
      }));

      const allUrls = [...staticPages, ...blogUrls];
      setSitemapUrls(allUrls);
      generateXml(allUrls);
    } catch (error) {
      console.error('Error fetching blog posts:', error);
      setSitemapUrls(staticPages);
      generateXml(staticPages);
    } finally {
      setLoading(false);
    }
  };

  const generateXml = (urls: SitemapUrl[]) => {
    const xml = `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
${urls.map(url => `  <url>
    <loc>${baseUrl}${url.loc}</loc>
    <lastmod>${url.lastmod}</lastmod>
    <changefreq>${url.changefreq}</changefreq>
    <priority>${url.priority}</priority>
  </url>`).join('\n')}
</urlset>`;
    setXmlContent(xml);
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(xmlContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Copy failed:', error);
    }
  };

  const downloadXml = () => {
    const blob = new Blob([xmlContent], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sitemap.xml';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">サイトマップを生成中...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-5xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">サイトマップ</h1>
              <p className="text-sm text-gray-500 mt-1">
                全{sitemapUrls.length}ページのURLを含むXMLサイトマップ
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={copyToClipboard}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className={`ri-${copied ? 'check' : 'file-copy'}-line`}></i>
                {copied ? 'コピー完了' : 'XMLをコピー'}
              </button>
              <button
                onClick={downloadXml}
                className="flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-download-line"></i>
                ダウンロード
              </button>
            </div>
          </div>

          {/* URL一覧 */}
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-3">URL一覧</h2>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">URL</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 w-32">最終更新</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 w-28">更新頻度</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 w-20">優先度</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {sitemapUrls.map((url, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <a 
                          href={`${baseUrl}${url.loc}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-orange-600 hover:text-orange-700 hover:underline"
                        >
                          {url.loc}
                        </a>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{url.lastmod}</td>
                      <td className="px-4 py-3 text-gray-600">{url.changefreq}</td>
                      <td className="px-4 py-3 text-gray-600">{url.priority}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* XMLプレビュー */}
          <div>
            <h2 className="text-lg font-semibold text-gray-800 mb-3">XMLプレビュー</h2>
            <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
              <pre className="text-sm text-green-400 font-mono whitespace-pre">{xmlContent}</pre>
            </div>
          </div>
        </div>

        {/* 使い方ガイド */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">
            <i className="ri-lightbulb-line text-orange-500 mr-2"></i>
            サイトマップの使い方
          </h2>
          <div className="space-y-4 text-sm text-gray-600">
            <div className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center font-medium">1</span>
              <div>
                <p className="font-medium text-gray-800">XMLファイルをダウンロード</p>
                <p>上のダウンロードボタンからsitemap.xmlをダウンロードします。</p>
              </div>
            </div>
            <div className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center font-medium">2</span>
              <div>
                <p className="font-medium text-gray-800">サーバーのルートに配置</p>
                <p>ダウンロードしたファイルをWebサイトのルートディレクトリに配置します。</p>
              </div>
            </div>
            <div className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center font-medium">3</span>
              <div>
                <p className="font-medium text-gray-800">Google Search Consoleに登録</p>
                <p>Google Search Consoleの「サイトマップ」セクションでURLを送信します。</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
